import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthIdentity, RegisterInput } from '@nx/domain';

export interface AuthResult {
  success: boolean;
  error: boolean;
  message: string | null;
}

export interface LoginPayload {
  email: string;
  password: string;
}

const initialResult: AuthResult = { success: false, error: false, message: null };

export interface AuthState {
  identity: AuthIdentity | null;
  isAuthInitialized: boolean;
  isAuthenticating: boolean;
  authResult: AuthResult;
}

const initialState: AuthState = {
  identity: null,
  isAuthInitialized: false,
  isAuthenticating: false,
  authResult: initialResult,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginRequested: (state, _action: PayloadAction<LoginPayload>) => {
      state.isAuthenticating = true;
      state.authResult = initialResult;
    },
    registerRequested: (state, _action: PayloadAction<RegisterInput>) => {
      state.isAuthenticating = true;
      state.authResult = initialResult;
    },
    logoutRequested: (state) => {
      state.isAuthenticating = true;
      state.authResult = initialResult;
    },
    authSucceeded: (
      state,
      action: PayloadAction<{ identity: AuthIdentity; message?: string | null }>,
    ) => {
      state.isAuthInitialized = true;
      state.isAuthenticating = false;
      state.identity = action.payload.identity;
      state.authResult = {
        success: true,
        error: false,
        message: action.payload.message ?? null,
      };
    },
    authFailed: (state, action: PayloadAction<{ message: string }>) => {
      state.isAuthInitialized = true;
      state.isAuthenticating = false;
      state.identity = null;
      state.authResult = { success: false, error: true, message: action.payload.message };
    },
    authSignedOut: (state) => {
      state.isAuthInitialized = true;
      state.isAuthenticating = false;
      state.identity = null;
      state.authResult = initialResult;
    },
    authCleared: (state) => {
      state.authResult = initialResult;
    },
  },
});

export const {
  loginRequested,
  registerRequested,
  logoutRequested,
  authSucceeded,
  authFailed,
  authSignedOut,
  authCleared,
} = authSlice.actions;

export default authSlice.reducer;
